import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Modal from "react-bootstrap/esm/Modal";
import Button from "react-bootstrap/esm/Button";
import Form from "react-bootstrap/esm/Form";
import Row from "react-bootstrap/esm/Row";
import Col from "react-bootstrap/esm/Col";
import Table from "react-bootstrap/esm/Table";
import Alert from "react-bootstrap/esm/Alert";
import Spinner from "react-bootstrap/esm/Spinner";
import type { ServiceType } from "../DTOs/SundayService";
import PlannedSermon from "../DTOs/PlannedSermon";
import { auth } from "../services/firebase";
import { saveSundayServiceFromPlannedSermon } from "../services/firestoreService";
import { SELECTABLE_SERVICE_TYPES } from "../utils/serviceType";
import { existingServiceKeys, planSeries, SeriesType, seriesSlotModes, WEEKDAY_NAMES_DE } from "../utils/serviceSeries";

interface Props {
    show: boolean;
    gemeindeId: string;
    /** The services already in the list, to skip what exists. */
    services: { date: Date; serviceType: ServiceType }[];
    onClose: () => void;
    onCreated: (count: number) => void;
}

// Monday first, as on a German calendar.
const WEEKDAY_ORDER = [1, 2, 3, 4, 5, 6, 0];

/**
 * Creates services for a period by weekday and service type (GOD-47). The
 * preview shows every date; services that exist already (same day and type)
 * are skipped, never overwritten.
 */
export default function ServiceSeriesDialog({ show, gemeindeId, services, onClose, onCreated }: Props) {
    const { t } = useTranslation();
    const [from, setFrom] = useState("");
    const [to, setTo] = useState("");
    const [weekdays, setWeekdays] = useState<number[]>([0]);
    const [type, setType] = useState<SeriesType>("auto");
    const [label, setLabel] = useState("");
    const [creating, setCreating] = useState(false);
    const [error, setError] = useState("");

    const existing = useMemo(() => existingServiceKeys(services), [services]);
    const plan = useMemo(() => (from && to ? planSeries(from, to, weekdays, type, existing) : null), [from, to, weekdays, type, existing]);
    const newEntries = plan?.entries.filter((e) => !e.exists) ?? [];
    const locale = t("calendar.locale") as string;

    const toggleWeekday = (day: number) =>
        setWeekdays((current) => (current.includes(day) ? current.filter((d) => d !== day) : [...current, day]));

    async function handleCreate() {
        setCreating(true);
        setError("");
        let created = 0;
        try {
            const userId = auth.currentUser?.uid ?? "";
            for (const entry of newEntries) {
                const modes = seriesSlotModes(entry.serviceType);
                const planned = new PlannedSermon(
                    entry.date,
                    entry.weekday,
                    entry.serviceType,
                    label.trim(),
                    "",
                    "",
                    modes.sermon1Mode,
                    "",
                    "",
                    modes.sermon2Mode,
                    userId,
                    new Date()
                );
                // Deterministic id per day and type; an existing document is left alone.
                if (await saveSundayServiceFromPlannedSermon(gemeindeId, planned)) created++;
            }
            onCreated(created);
        } catch (err: any) {
            setError(err?.message ?? t("errors.anErrorOccurred"));
            if (created > 0) onCreated(created);
        }
        setCreating(false);
    }

    return (
        <Modal show={show} onHide={creating ? undefined : onClose} size="lg" centered>
            <Modal.Header closeButton={!creating}>
                <Modal.Title>{t("series.title")}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <p className="text-muted small">{t("series.intro")}</p>
                <Row className="g-3 mb-3">
                    <Col sm={6}>
                        <Form.Group controlId="series.from">
                            <Form.Label>{t("series.from")}</Form.Label>
                            <Form.Control type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
                        </Form.Group>
                    </Col>
                    <Col sm={6}>
                        <Form.Group controlId="series.to">
                            <Form.Label>{t("series.to")}</Form.Label>
                            <Form.Control type="date" value={to} onChange={(e) => setTo(e.target.value)} />
                        </Form.Group>
                    </Col>
                    <Col sm={12}>
                        <Form.Label className="d-block">{t("series.weekdays")}</Form.Label>
                        {WEEKDAY_ORDER.map((day) => (
                            <Form.Check
                                key={day}
                                inline
                                id={`series.weekday.${day}`}
                                label={WEEKDAY_NAMES_DE[day].slice(0, 2)}
                                checked={weekdays.includes(day)}
                                onChange={() => toggleWeekday(day)}
                            />
                        ))}
                    </Col>
                    <Col sm={6}>
                        <Form.Group controlId="series.type">
                            <Form.Label>{t("series.type")}</Form.Label>
                            <Form.Select value={type} onChange={(e) => setType(e.target.value as SeriesType)}>
                                <option value="auto">{t("series.typeAuto")}</option>
                                {SELECTABLE_SERVICE_TYPES.map((st) => (
                                    <option key={st} value={st}>
                                        {t(`serviceTypes.${st}`)}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                    </Col>
                    <Col sm={6}>
                        <Form.Group controlId="series.label">
                            <Form.Label>{t("series.label")}</Form.Label>
                            <Form.Control value={label} maxLength={80} placeholder={t("series.labelPlaceholder") as string} onChange={(e) => setLabel(e.target.value)} />
                        </Form.Group>
                    </Col>
                </Row>

                {plan?.error && <Alert variant="warning">{t(`series.errors.${plan.error}`)}</Alert>}
                {error && <Alert variant="danger">{error}</Alert>}
                {plan && !plan.error && (
                    <>
                        <p className="mb-2">
                            {t("series.summary", { count: newEntries.length, existing: plan.entries.length - newEntries.length })}
                        </p>
                        <div style={{ maxHeight: 280, overflowY: "auto" }}>
                            <Table size="sm" bordered data-testid="series-preview">
                                <tbody>
                                    {plan.entries.map((entry) => (
                                        <tr key={`${entry.date}-${entry.serviceType}`} className={entry.exists ? "text-muted" : undefined}>
                                            <td>{new Date(`${entry.date}T12:00:00`).toLocaleDateString(locale, { weekday: "short", day: "numeric", month: "long", year: "numeric" })}</td>
                                            <td>{t(`serviceTypes.${entry.serviceType}`)}</td>
                                            <td>{entry.exists ? t("series.exists") : t("series.new")}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    </>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onClose} disabled={creating}>
                    {t("common.cancel")}
                </Button>
                <Button onClick={handleCreate} disabled={creating || newEntries.length === 0}>
                    {creating ? <Spinner animation="border" size="sm" /> : t("series.create", { count: newEntries.length })}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}
